import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { DateEditor, CohortDateEmailConfig, CohortEmailConfigs } from './CohortEmailDialog';
import { COHORT_DATE_COUNT } from '../Constants';

const PLACEHOLDER_COHORT_ID = '__defaults__';
const DEFAULT_CONFIG: CohortDateEmailConfig = { subject: '', body: '<p></p>', attachments: [] };

interface EmailDefaultsDialogProps {
  open: boolean;
  onClose: () => void;
  getDefaults: () => Promise<CohortEmailConfigs>;
  onSave: (dateIndex: string, config: CohortDateEmailConfig) => Promise<void>;
  onUploadAttachment: (
    cohortId: string,
    fileName: string,
    fileType: string
  ) => Promise<{ uploadUrl: string; fileKey: string }>;
}

export const EmailDefaultsDialog: React.FC<EmailDefaultsDialogProps> = ({
  open,
  onClose,
  getDefaults,
  onSave,
  onUploadAttachment,
}) => {
  const { t } = useTranslation();
  const dateIndices = Array.from({ length: COHORT_DATE_COUNT }, (_, i) => i + 1);

  const [tabIndex, setTabIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [configs, setConfigs] = React.useState<CohortEmailConfigs>({});

  React.useEffect(() => {
    if (!open) return;
    setTabIndex(0);
    setLoading(true);
    getDefaults()
      .then((defaults) => setConfigs(defaults))
      .finally(() => setLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const isReminderTab = tabIndex === dateIndices.length;
  const activeDateIndex = isReminderTab ? undefined : dateIndices[tabIndex];
  const activeDateKey = isReminderTab ? 'reminder' : String(activeDateIndex);

  const handleChange = (
    dateKey: number | string,
    subject: string,
    body: string,
    attachments: CohortDateEmailConfig['attachments']
  ) => {
    setConfigs((prev) => ({ ...prev, [String(dateKey)]: { subject, body, attachments } }));
  };

  const handleSave = async () => {
    const config = configs[activeDateKey] ?? DEFAULT_CONFIG;
    setSaving(true);
    await onSave(activeDateKey, config);
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('cohorts.email_defaults_title')}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <CircularProgress size={24} sx={{ display: 'block', mx: 'auto', my: 4 }} />
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('cohorts.email_defaults_desc')}
            </Typography>
            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              {dateIndices.map((n) => (
                <Tab key={n} label={t('cohorts.date_n', { n: String(n) })} />
              ))}
              <Tab key="reminder" label={t('cohorts.email_reminder_tab')} />
            </Tabs>
            <DateEditor
              key={activeDateKey}
              initialSubject={configs[activeDateKey]?.subject ?? ''}
              initialBody={configs[activeDateKey]?.body ?? '<p></p>'}
              initialAttachments={configs[activeDateKey]?.attachments ?? []}
              cohortId={PLACEHOLDER_COHORT_ID}
              dateIndex={activeDateKey}
              showResponsesCount={isReminderTab}
              onUploadAttachment={onUploadAttachment}
              onChange={(subject, body, attachments) =>
                handleChange(activeDateKey, subject, body, attachments)
              }
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
          {saving ? (
            <CircularProgress size={20} />
          ) : isReminderTab ? (
            t('cohorts.email_save_reminder')
          ) : (
            t('cohorts.email_save_date', { n: String(activeDateIndex) })
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
